import { useState } from "react";
import {
  Button,
  DataButton,
  InputContainer,
  InputOption,
  InputSelect,
  SingleFieldDialog,
  SingleFieldDialogCTA,
  SingleFieldDialogHeader,
  SingleFieldDialogImage,
} from "@sellar-io/components";
import { Icon } from "../../../components";
import { useVolumeDiscountsModeUpdate } from "../../../hooks/api/v2/volumeDiscounts/useVolumeDiscountsModeUpdate";
import { Alert } from "~/components/alert/Alert";
import { useVolumeDiscountsModeAssignments } from "../../../hooks/api/v2/volumeDiscounts/useVolumeDiscountsModeAssignments";

const deliveryMethodLabel = (fulfillmentType: string | null) => {
  if (fulfillmentType === null) return "Default (all delivery methods)";
  if (fulfillmentType === "sellarDistribution") return "Sellar Distribution";
  return fulfillmentType.charAt(0).toUpperCase() + fulfillmentType.slice(1);
};

type VolumeDiscountAdvancedSetupDialogProps = {
  active: boolean;
  onDismiss: () => void;
  onEnabled?: () => void;
};

export const ConditionalVolumeDiscountsPrompt = ({
  title = "Need to apply different volume discounts for different delivery methods?",
  message = "You can set up conditional volume discounts to apply different sets for direct, courier, haulier, collection and Sellar Distribution orders.",
  btnText = "Set up conditional volume discounts",
  onClick,
}: {
  title?: string;
  message?: string;
  btnText?: string;
  onClick: () => void;
}) => (
  <Alert
    type="sellar"
    title={title}
    message={message}
    className="border-zinc-300 bg-zinc-50"
  >
    <DataButton
      border
      className="flex w-fit items-center gap-1"
      onClick={onClick}
    >
      {btnText}
      <Icon icon="ARROW_TOP_RIGHT" size={0.7} />
    </DataButton>
  </Alert>
);

export const VolumeDiscountAdvancedSetupDialog = ({
  active,
  onDismiss,
  onEnabled,
}: VolumeDiscountAdvancedSetupDialogProps) => {
  const [page, setPage] = useState<"introduction" | "setup">("introduction");
  const modeMutation = useVolumeDiscountsModeUpdate();

  const dismiss = () => {
    setPage("introduction");
    onDismiss();
  };

  return (
    <SingleFieldDialog active={active} onDismiss={dismiss} size="large">
      {page === "introduction" ? (
        <>
          <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/terms-basic-advanced/basic-vd-1.png" />
          <SingleFieldDialogHeader
            title="Set conditional volume discounts on your price lists"
            description="Some examples of when it's useful to have different volume discounts for different delivery methods are shown above"
            icon="INFORMATION_OUTLINE"
            iconClassName="mb-auto bg-blue-50 text-blue-600"
          />
          <SingleFieldDialogCTA>
            <Button color="transparent" onClick={dismiss}>
              Dismiss
            </Button>
            <Button
              className="flex items-center gap-1"
              onClick={() => setPage("setup")}
            >
              Next <Icon icon="CHEVRON_RIGHT" size={0.8} />
            </Button>
          </SingleFieldDialogCTA>
        </>
      ) : (
        <>
          <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/terms-basic-advanced/basic-vd-2.png" />
          <SingleFieldDialogHeader
            title="Apply multiple volume sets to one price list"
            description="A price list can use multiple volume discount sets, provided they apply to different delivery methods. Two sets can't apply to the same delivery method."
            icon="INFORMATION_OUTLINE"
            iconClassName="mb-auto bg-blue-50 text-blue-600"
          />
          <SingleFieldDialogCTA>
            <Button
              className="mr-auto flex items-center gap-1"
              color="transparent"
              onClick={() => setPage("introduction")}
            >
              <Icon icon="CHEVRON_LEFT" size={0.8} /> Back
            </Button>
            <Button
              loading={modeMutation.isPending}
              onClick={() =>
                modeMutation.mutate(
                  { mode: "advanced" },
                  {
                    onSuccess: () => {
                      dismiss();
                      onEnabled?.();
                    },
                  },
                )
              }
            >
              Turn on
              <Icon icon="CHECK_CIRCLE" />
            </Button>
          </SingleFieldDialogCTA>
        </>
      )}
    </SingleFieldDialog>
  );
};

type RevertToBasicVolumeDiscountDialogProps = {
  active: boolean;
  onDismiss: () => void;
};

export const RevertToBasicVolumeDiscountDialog = ({
  active,
  onDismiss,
}: RevertToBasicVolumeDiscountDialogProps) => {
  const [page, setPage] = useState<"introduction" | "select">("introduction");
  const [selections, setSelections] = useState<Record<number, number>>({});
  const [error, setError] = useState("");
  const modeMutation = useVolumeDiscountsModeUpdate();
  const assignmentsQuery = useVolumeDiscountsModeAssignments({
    enabled: active,
  });
  const affected = assignmentsQuery.data ?? [];
  const revert = () => {
    if (affected.some((item) => !selections[item.priceListId])) return;
    setError("");
    modeMutation.mutate(
      {
        mode: "basic",
        selections: affected.map((item) => ({
          priceListId: item.priceListId,
          volumeDiscountId: selections[item.priceListId]!,
        })),
      },
      {
        onSuccess: dismiss,
        onError: () => {
          setError("Could not revert. Please reload and try again.");
          assignmentsQuery.refetch();
        },
      },
    );
  };

  const dismiss = () => {
    setPage("introduction");
    setSelections({});
    setError("");
    onDismiss();
  };

  if (page === "introduction") {
    return (
      <SingleFieldDialog active={active} onDismiss={dismiss} size="large">
        <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/terms-basic-advanced/basic-vd-3.png" />
        <SingleFieldDialogHeader
          title="You can revert back to a simpler volume discount set up"
          description="By choosing this you will no longer be able to specify which delivery method needs to be chosen to qualify for each volume discount. You will have just one discount set to apply to each price list"
          icon="INFORMATION_OUTLINE"
          iconClassName="mb-auto bg-blue-50 text-blue-600"
        />
        {(assignmentsQuery.isError || error) && (
          <p className="mx-4 text-sm text-red-600">
            {error ||
              "Could not load price list assignments. Please try again."}
          </p>
        )}
        <SingleFieldDialogCTA>
          <Button color="transparent" onClick={dismiss}>
            Dismiss
          </Button>
          <Button
            className="flex items-center gap-1"
            disabled={assignmentsQuery.isFetching || assignmentsQuery.isError}
            loading={modeMutation.isPending}
            onClick={() => (affected.length ? setPage("select") : revert())}
          >
            {affected.length ? "Next" : "Confirm"}
            <Icon
              icon={affected.length ? "CHEVRON_RIGHT" : "CHECK_CIRCLE"}
              size={0.8}
            />
          </Button>
        </SingleFieldDialogCTA>
      </SingleFieldDialog>
    );
  }

  return (
    <SingleFieldDialog active={active} onDismiss={dismiss} size="large">
      <SingleFieldDialogHeader
        title="Some of your price lists still have conditional volume discounts set"
        description="In order to revert to a simple volume discount set up, please choose which volume discounts to set on the following price lists"
      />
      <div className="flex max-h-80 flex-col gap-4 overflow-y-auto">
        {affected.map((item) => (
          <div key={item.priceListId} className="flex flex-col gap-2">
            <span className="font-semibold">{item.priceListName}</span>
            <ul className="flex list-disc flex-col gap-1 rounded-lg bg-zinc-100 p-3 pl-9 text-sm text-zinc-700">
              {item.sets.map((set) => (
                <li key={set.id}>
                  <span className="font-semibold">{set.name}</span>
                  {" → "}
                  {item.assignments
                    .filter(
                      (assignment) => assignment.volumeDiscountId === set.id,
                    )
                    .map((assignment) =>
                      deliveryMethodLabel(assignment.fulfillmentType),
                    )
                    .join(", ")}
                </li>
              ))}
            </ul>
            <InputContainer>
              <InputSelect
                id={`volume-discount-default-${item.priceListId}`}
                value={selections[item.priceListId]?.toString() ?? ""}
                onChange={(event) =>
                  setSelections((current) => ({
                    ...current,
                    [item.priceListId]: Number(event.target.value),
                  }))
                }
                placeholder="Select..."
              >
                <InputOption value={undefined}>Select...</InputOption>
                {item.sets.map((set) => (
                  <InputOption key={set.id} value={set.id.toString()}>
                    {set.name}
                  </InputOption>
                ))}
              </InputSelect>
            </InputContainer>
          </div>
        ))}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
      <SingleFieldDialogCTA>
        <Button
          disabled={affected.some((item) => !selections[item.priceListId])}
          loading={modeMutation.isPending}
          onClick={revert}
        >
          Confirm
        </Button>
      </SingleFieldDialogCTA>
    </SingleFieldDialog>
  );
};
